import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import notificationService from './notificationService.js';

const POLL_MS = 60_000;

/** An approval → its request detail; an episode → its page. Anything else has nowhere to link, so it renders as plain text. */
export function linkFor(n) {
  if (n.subjectType === 'approval_request') return `/approvals/${n.subjectId}`;
  // X-5a — the episode may live in another workspace; the workspace page's
  // read-only cross-workspace view (?workspaceId=) is what opens it.
  if (n.subjectType === 'episode') {
    return `/episodes/${n.subjectId}${n.workspaceId ? `?workspaceId=${encodeURIComponent(n.workspaceId)}` : ''}`;
  }
  return null;
}

const KIND_LABEL = {
  approval_requested: 'Approval requested',
  approval_approved: 'Approved',
  approval_rejected: 'Rejected',
  approval_changes_requested: 'Changes requested',
  approval_delegated: 'Delegated to you',
  approval_mention: 'You were mentioned',
  approval_reminder: 'Approval overdue',
  approval_no_eligible_approver: 'Nobody can approve',
  episode_paused: 'Production paused',
  episode_resumed: 'Production resumed',
  spend_approved_while_paused: 'Spend approved while paused',
  production_start_failed: 'Production failed to start',
};

/** Wave 3 (audit F19) — what happened, to which episode, for how much. */
export function labelFor(n) {
  const what = KIND_LABEL[n.kind] || (n.kind || '').replace(/_/g, ' ');
  const title = n.subjectTitle ? ` — ${n.subjectTitle}` : '';
  const amount = n.payload?.amountCents != null ? ` · $${(Number(n.payload.amountCents) / 100).toFixed(2)}` : '';
  const reason = n.payload?.reason ? ` (${String(n.payload.reason).replace(/_/g, ' ')})` : '';
  return `${what}${title}${amount}${reason}`;
}

/**
 * Navbar notification bell. Polls unread notifications every 60s, pausing
 * while the tab is hidden — a background tab has no viewer to notify, and
 * polling it anyway is pure load across every open tab.
 */
export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(() => {
    notificationService.listUnread()
      .then((list) => { if (mounted.current) setNotifications(list || []); })
      .catch(() => { /* a bell that cannot reach the server just shows nothing new */ });
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(() => {
      if (document.hidden) return;
      load();
    }, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load]);

  const markAllRead = () => {
    const ids = notifications.map((n) => n.id);
    if (!ids.length) return;
    notificationService.markRead(ids)
      .then(() => setNotifications([]))
      .catch(() => {});
  };

  // Audit F19 — opening one marks it read (the bell no longer stays red after everything is handled).
  const openOne = (n) => {
    setOpen(false);
    setNotifications((list) => list.filter((x) => x.id !== n.id));
    notificationService.markRead([n.id]).catch(() => {});
  };

  const count = notifications.length;
  const latest = notifications.slice(0, 10);

  return (
    <div className="notification-bell dropdown">
      <button
        type="button"
        className="btn btn-link text-light position-relative"
        aria-label={`Notifications${count ? `, ${count} unread` : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <i className="fa fa-bell" aria-hidden="true" />
        {count > 0 && <span className="badge bg-danger rounded-pill ms-1">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div className="dropdown-menu dropdown-menu-end show p-2" style={{ minWidth: '280px' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Notifications</strong>
            <button type="button" className="btn btn-sm btn-link" onClick={markAllRead} disabled={!count}>
              Mark all read
            </button>
          </div>
          {latest.length === 0 && <p className="text-muted mb-0">No unread notifications.</p>}
          <ul className="list-unstyled mb-0">
            {latest.map((n) => {
              const href = linkFor(n);
              return (
                <li key={n.id} className="py-1 border-top">
                  {href ? (
                    <Link to={href} onClick={() => openOne(n)}>{labelFor(n)}</Link>
                  ) : (
                    <span>{labelFor(n)}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
